import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import moment from 'moment';
import Tooltip from 'components/Tooltip';
import { ExpiryModalShell } from './ExpiryDateModal';
import { historyType, getHistoryName } from 'views/components/helpers';
import { faClock } from '@fortawesome/pro-duotone-svg-icons';

// View-only modal listing each reference from the application and how its
// response compares against what the delegate claimed

const GREY = '#6A728F';
const AMBER = '#f68f1f';

// Date matching between what the delegate claimed on the application and
// what the reference responded with
const getDateMatchStatus = (
  item: any
): {
  status: 'no-response' | 'verified' | 'close-match' | 'mismatch';
  color: string;
  background: string;
  label: string;
  description: string;
} => {
  const applicationDates =
    item?.employer?.application?.dates ??
    item?.education?.application?.dates ??
    item?.selfEmployed?.application?.dates ??
    item?.unemployed?.application?.dates ??
    [];

  const responseDates =
    item?.employer?.response?.datesEmployed ??
    item?.education?.response?.dates ??
    item?.selfEmployed?.response?.dates ??
    item?.unemployed?.response?.alternateDates ??
    null;

  if (
    applicationDates.length === 0 ||
    !responseDates ||
    responseDates.length === 0
  ) {
    return {
      status: 'no-response',
      color: AMBER,
      background: 'rgba(246, 143, 31, 0.12)',
      label: 'Response not received',
      description: 'Waiting for response'
    };
  }

  const appStartDate = moment(applicationDates[0].startDate).format(
    'DD/MM/YYYY'
  );
  const appEndDate = moment(applicationDates[0].endDate).format('DD/MM/YYYY');
  const respStartDate = moment(responseDates[0].startDate).format('DD/MM/YYYY');
  const respEndDate = moment(responseDates[0].endDate).format('DD/MM/YYYY');

  const datesMatch =
    appStartDate === respStartDate && appEndDate === respEndDate;
  const isUnemployedAccurate =
    item?.unemployed?.response?.datesAccurate === true;

  // Check if dates are within 10 days of each other
  const appStart = moment(applicationDates[0].startDate);
  const appEnd = moment(applicationDates[0].endDate);
  const respStart = moment(responseDates[0].startDate);
  const respEnd = moment(responseDates[0].endDate);

  const startDiffDays = Math.abs(appStart.diff(respStart, 'days'));
  const endDiffDays = Math.abs(appEnd.diff(respEnd, 'days'));
  const isCloseMatch = startDiffDays <= 10 && endDiffDays <= 10 && !datesMatch;

  if (datesMatch || isUnemployedAccurate) {
    return {
      status: 'verified',
      color: 'rgb(23 165 29)',
      background: 'rgba(23, 165, 29, 0.12)',
      label: 'Verified',
      description: 'Response dates match the application dates'
    };
  } else if (isCloseMatch) {
    return {
      status: 'close-match',
      color: '#1fa7d4',
      background: 'rgba(31, 167, 212, 0.12)',
      label: 'Close match',
      description: 'Response dates are close match'
    };
  } else {
    return {
      status: 'mismatch',
      color: AMBER,
      background: 'rgba(246, 143, 31, 0.12)',
      label: 'Date mismatch',
      description: "Response dates don't match"
    };
  }
};

const useStyles = createUseStyles((theme: Theme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  row: {
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    padding: '12px 16px'
  },
  topLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  orgBlock: {
    minWidth: 0
  },
  orgName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1C1C1C',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  orgType: {
    fontSize: 12,
    fontWeight: 500,
    color: GREY,
    margin: 0
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  dates: {
    marginTop: 8,
    fontSize: '0.75rem',
    color: GREY
  },
  responseLine: {
    fontWeight: 500
  },
  sentLog: {
    marginTop: 6,
    fontSize: '0.7rem',
    color: '#9aa0b8'
  },
  empty: {
    padding: '20px 0',
    textAlign: 'center',
    color: GREY,
    fontSize: '0.8rem'
  }
}));

const typeLabel = (item: any) =>
  historyType(item) === 'employment'
    ? 'Employment'
    : historyType(item) === 'education'
    ? 'Education'
    : historyType(item) === 'self-employment'
    ? 'Self Employment'
    : historyType(item) === 'unemployed'
    ? 'Unemployed'
    : 'Unknown';

const dateRange = (dates: any[] | null | undefined) => {
  if (!dates || dates.length === 0) return null;
  return `${moment(dates[0].startDate).format('DD/MM/YYYY')} – ${moment(
    dates[0].endDate
  ).format('DD/MM/YYYY')}`;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  history: readonly any[];
};

export default function ReferencesModal({ isOpen, onClose, history }: Props) {
  const classes = useStyles();

  return (
    <ExpiryModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={faClock}
      title="References"
      subtitle="Each reference from the application"
      width={560}
    >
      <div className={classes.list}>
        {history.map((item: any) => {
          const applicationRange = dateRange(
            item?.employer?.application?.dates ??
              item?.education?.application?.dates ??
              item?.selfEmployed?.application?.dates ??
              item?.unemployed?.application?.dates
          );
          const responseRange = dateRange(
            item?.employer?.response?.datesEmployed ??
              item?.education?.response?.dates ??
              item?.selfEmployed?.response?.dates ??
              item?.unemployed?.response?.alternateDates
          );
          const matchStatus = getDateMatchStatus(item);
          const sendLogs = item?.responseSendLogs ?? [];

          return (
            <div key={item.uuid} className={classes.row}>
              <div className={classes.topLine}>
                <div className={classes.orgBlock}>
                  <p className={classes.orgName}>{getHistoryName(item)}</p>
                  <p className={classes.orgType}>{typeLabel(item)}</p>
                </div>
                <Tooltip text={matchStatus.description}>
                  <span
                    className={classes.statusPill}
                    style={{
                      color: matchStatus.color,
                      background: matchStatus.background
                    }}
                  >
                    {matchStatus.label}
                  </span>
                </Tooltip>
              </div>
              <div className={classes.dates}>
                <div>Application: {applicationRange ?? '-'}</div>
                {responseRange && (
                  <div
                    className={classes.responseLine}
                    style={{ color: matchStatus.color }}
                  >
                    Response: {responseRange}
                  </div>
                )}
              </div>
              {sendLogs.length > 0 && (
                <div className={classes.sentLog}>
                  {sendLogs.map((log: any, index: number) => (
                    <div key={index}>
                      Sent to {log.emailAddress} {moment(log.sentAt).fromNow()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {history.length === 0 && (
          <div className={classes.empty}>No references yet</div>
        )}
      </div>
    </ExpiryModalShell>
  );
}
